import { useQuery, type Query } from '@tanstack/react-query'
import { PaytrieAPI } from '@paytrie/client'

import { authService } from '@/services/auth'
import { configurePaytrieClient } from '@/utils/apiClient'

type Requirements = PaytrieAPI.UserRequirements

type Options = {
  refetchInterval?:
    | number
    | false
    | ((query: Query<Requirements>) => number | false)
}

const STALE_TIME_MS = 5 * 60 * 1000

export class UnauthenticatedError extends Error {
  constructor() {
    super('Unauthenticated')
    this.name = 'UnauthenticatedError'
  }
}

export const useOnboardingRequirements = (
  enabled: boolean,
  options: Options = {}
) => {
  const token = enabled ? authService.getToken() : null

  return useQuery({
    queryKey: ['v2', 'users', 'me', 'requirements', token],
    queryFn: async () => {
      if (!configurePaytrieClient()) {
        throw new UnauthenticatedError()
      }

      const res = await PaytrieAPI.getCurrentUserRequirements()

      if (res.status === 401) {
        authService.clearToken()
        throw new UnauthenticatedError()
      }

      if (res.status !== 200) {
        throw new Error(
          `Failed to load user requirements (status ${res.status})`
        )
      }

      return res.data.data
    },
    enabled,
    retry: (failureCount, error) => {
      if (error instanceof UnauthenticatedError) return false
      return failureCount < 2
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    staleTime: STALE_TIME_MS,
    refetchInterval: options.refetchInterval ?? false,
  })
}
